import { useEffect, useMemo, useState } from 'react';
import { X, AlertCircle, Search, Info } from 'lucide-react';

import { createWatchlistEntry, updateWatchlistEntry } from '@/lib/queries';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useDataState } from '@/hooks/use-data-state';
import { getMarketDataProvider } from '@/lib/market-data';
import { applicableStrategiesForSubtype, inferAssetSubtype } from '@/lib/v51/asset-subtype';
import { STRATEGY_EXPLANATIONS } from '@/lib/glossary';
import {
  ASSET_CLASSES,
  WATCHLIST_LAYERS,
  type AssetClass,
  type Strategy,
  type WatchlistEntry,
  type WatchlistLayer,
} from '@/types';

const ASSET_CLASS_LABEL: Record<AssetClass, string> = {
  stock: 'Aktie',
  etf: 'ETF',
  crypto: 'Krypto',
  commodity_etf: 'Rohstoff-ETF',
};

const LAYER_LABEL: Record<WatchlistLayer, string> = {
  // V5-klassisch
  swing: 'Swing',
  day_swing: 'Day-Swing',
  longterm_build: 'Langfrist-Aufbau',
  dividend: 'Dividenden',
  crypto_accumulation: 'Krypto-Akku',
  sector_etf: 'Sector-ETF',
  open_position: 'Offene Trades',
  // V5.1
  core: 'Core',
  tactical: 'Tactical',
  theme: 'Theme',
  defensive: 'Defensive',
  crypto: 'Krypto',
  bestand: 'Bestand / Sparplan',
  // Nie tatsächlich waehlbar (siehe unten) — nur fuer Typ-Vollstaendigkeit.
  fokus: 'Fokus',
};

/**
 * 20.09.2026 — „Fokus" ist eine SICHT auf die Watchlist (offene Position ∪
 * Rolle tactical), keine speicherbare Rolle. Stünde sie hier zur Wahl, könnte
 * ein Eintrag `watchlist_role = 'fokus'` bekommen und wäre danach in KEINEM
 * Tab mehr sichtbar — weder unter „Fokus" (der Filter prüft offene Position
 * oder `tactical`, nicht den String „fokus") noch unter irgendeiner echten
 * Rolle. Deshalb fehlt sie in der Auswahl, obwohl sie in `WATCHLIST_LAYERS`
 * steht.
 */
const WAEHLBARE_LAYER = WATCHLIST_LAYERS.filter((l) => l !== 'fokus');

interface AddSymbolDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  defaultLayer: WatchlistLayer;
  defaultAssetClass: AssetClass;
  strategies: Strategy[];
  /** Wenn gesetzt, läuft der Dialog im Edit-Modus für diesen Eintrag. */
  editEntry?: WatchlistEntry | null;
  /** Audit 15.06.: Symbol im Add-Modus vorausfüllen (z.B. aus dem Experten-Radar). */
  initialSymbol?: string | null;
}

export function AddSymbolDialog({
  open,
  onClose,
  onSaved,
  defaultLayer,
  defaultAssetClass,
  strategies,
  editEntry,
  initialSymbol,
}: AddSymbolDialogProps) {
  const isEditMode = Boolean(editEntry);
  const [symbol, setSymbol] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [assetClass, setAssetClass] = useState<AssetClass>(defaultAssetClass);
  const [layer, setLayer] = useState<WatchlistLayer>(defaultLayer);
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Reset on open — füllt Edit-Werte aus, wenn editEntry gesetzt
  useEffect(() => {
    if (open) {
      if (editEntry) {
        setSymbol(editEntry.symbol);
        setDisplayName(editEntry.display_name);
        setAssetClass(editEntry.asset_class);
        setLayer(editEntry.layer);
        setSelectedStrategies(editEntry.applicable_strategies);
        setNotes(editEntry.notes);
        setTagsRaw(editEntry.user_tags.join(', '));
      } else {
        setSymbol(initialSymbol ?? '');
        setDisplayName('');
        setAssetClass(defaultAssetClass);
        setLayer(defaultLayer);
        setSelectedStrategies([]);
        setNotes('');
        setTagsRaw('');
      }
      setSubmitting(false);
      setErrorMsg(null);
    }
  }, [open, defaultAssetClass, defaultLayer, editEntry, initialSymbol]);

  // Welle 9-Fix: matchingStrategies wird nicht mehr gerendert (V5.1 Auto-Mapping).
  // Wir bauen sie noch, damit die Referenz in Symbol-Auto-Select erhalten bleibt.
  const matchingStrategies = useMemo(
    () => strategies.filter((s) => s.asset_class === assetClass && s.is_active),
    [strategies, assetClass]
  );
  void matchingStrategies; // bewusst ungenutzt — V5.1 macht Auto-Mapping

  // Live-Symbol-Lookup über Marktdaten-Provider
  const provider = useMemo(() => getMarketDataProvider(), []);
  const debouncedQuery = useDebouncedValue(symbol.trim(), 300);
  const showLookup = open && provider.is_live && debouncedQuery.length >= 1;

  const lookup = useDataState({
    queryKey: ['symbol-lookup', debouncedQuery],
    queryFn: (signal) => provider.lookupSymbol(debouncedQuery, signal).then((s) => s.data),
    enabled: showLookup,
    ttlSeconds: 60 * 5,
  });

  if (!open) return null;

  const symbolValid = /^[A-Za-z0-9.\-/_]{1,15}$/.test(symbol.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbolValid || submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const tags = tagsRaw
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      if (isEditMode && editEntry) {
        // Edit: nur Metadaten anpassen, Symbol/Asset-Klasse/Layer bleiben fix
        await updateWatchlistEntry(editEntry.id, {
          display_name: displayName.trim() || editEntry.symbol,
          applicable_strategies: selectedStrategies,
          notes: notes.trim(),
          user_tags: tags,
        });
      } else {
        await createWatchlistEntry({
          symbol: symbol.trim(),
          asset_class: assetClass,
          layer,
          display_name: displayName.trim() || undefined,
          applicable_strategies: selectedStrategies,
          notes: notes.trim(),
          user_tags: tags,
        });
      }
      onSaved();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-symbol-title"
    >
      {/* W81 Mobile-Fix — max-h-[90dvh] statt 90vh: dynamic viewport beruecksichtigt
          Mobile-Browser-Adressleiste (auf iOS Safari sind 90vh oft groesser als
          tatsaechlich sichtbarer Bereich), Footer-Submit-Button rutscht sonst
          unter den Viewport. */}
      <div className="w-full max-w-lg rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl max-h-[90dvh] flex flex-col">
        <header className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 id="add-symbol-title" className="text-base font-semibold">
            {isEditMode ? `${editEntry?.symbol} bearbeiten` : 'Symbol hinzufügen'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 focus-ring"
            aria-label="Schließen"
          >
            <X className="size-4" />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="overflow-y-auto p-5 space-y-4">
          {/* Symbol mit optionalem Live-Lookup */}
          <Field
            label="Ticker / Symbol"
            hint={
              provider.is_live
                ? 'Tippe Ticker oder Firmenname — die Liste schlägt Treffer vor.'
                : 'Z.B. AAPL, BTC-USD, XLE, BMW.DE (kein Live-Lookup, Marktdaten-Provider nicht aktiv).'
            }
            required
          >
            <div className="relative">
              <input
                type="text"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                autoFocus={!isEditMode}
                disabled={isEditMode}
                placeholder={provider.is_live ? 'AAPL oder Apple…' : 'AAPL'}
                className={cn(
                  'w-full rounded-md bg-zinc-900 border px-3 py-2 pr-9 text-sm font-mono focus-ring',
                  isEditMode && 'opacity-60 cursor-not-allowed',
                  symbol && !symbolValid
                    ? 'border-red-700 focus-visible:ring-red-500'
                    : 'border-zinc-700 focus-visible:ring-blue-500'
                )}
              />
              {showLookup && (
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
              )}
            </div>
            {symbol && !symbolValid && (
              <div className="mt-1 text-xs text-red-300 flex items-center gap-1">
                <AlertCircle className="size-3" />
                Ungültiges Symbol-Format.
              </div>
            )}
            {showLookup && lookup.state.status === 'success' && lookup.state.data && (
              <ul className="mt-2 max-h-44 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-900/80 divide-y divide-zinc-800">
                {lookup.state.data.map((s) => (
                  <li key={`${s.symbol}-${s.display_name}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setSymbol(s.symbol);
                        setDisplayName(s.display_name);
                        setAssetClass(s.asset_class);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-zinc-800 focus-ring"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-mono text-sm text-zinc-100">{s.symbol}</span>
                        <span className="text-[10px] uppercase text-zinc-500">
                          {s.asset_class}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-400 truncate">{s.display_name}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showLookup && lookup.state.status === 'no_data' && (
              <div className="mt-2 text-[11px] text-zinc-500 italic">
                Keine Treffer für „{debouncedQuery}".
              </div>
            )}
            {showLookup && lookup.state.status === 'loading' && (
              <div className="mt-2 text-[11px] text-zinc-500">Suche läuft…</div>
            )}
          </Field>

          {/* Display-Name */}
          <Field
            label="Bezeichnung (optional)"
            hint="Wird automatisch ausgefüllt, sobald Marktdaten angebunden sind."
          >
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Apple Inc."
              className="w-full rounded-md bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm focus-ring"
            />
          </Field>

          {/* Asset-Klasse + Layer (im Edit-Modus fix, weil Teil der Identität) */}
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Asset-Klasse"
              required
              hint={isEditMode ? 'Im Edit-Modus fix.' : undefined}
            >
              <select
                value={assetClass}
                onChange={(e) => setAssetClass(e.target.value as AssetClass)}
                disabled={isEditMode}
                className={cn(
                  'w-full rounded-md bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm focus-ring',
                  isEditMode && 'opacity-60 cursor-not-allowed'
                )}
              >
                {ASSET_CLASSES.map((c) => (
                  <option key={c} value={c}>
                    {ASSET_CLASS_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Watchlist-Layer"
              required
              hint={isEditMode ? 'Im Edit-Modus fix.' : undefined}
            >
              <select
                value={layer}
                onChange={(e) => setLayer(e.target.value as WatchlistLayer)}
                disabled={isEditMode}
                className={cn(
                  'w-full rounded-md bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm focus-ring',
                  isEditMode && 'opacity-60 cursor-not-allowed'
                )}
              >
                {WAEHLBARE_LAYER.map((l) => (
                  <option key={l} value={l}>
                    {LAYER_LABEL[l]}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Welle 9-Fix: V5.1-Multi-Score ordnet Strategien automatisch
              anhand Asset-Klasse + Subtype + Theme zu — kein manuelles Auswählen mehr.
              Wir zeigen, welche Strategien für dieses Asset greifen werden. */}
          <Field
            label="Strategie-Zuordnung (V5.1)"
            hint="Strategien werden automatisch anhand Asset-Klasse, Subtype und Themen ausgewählt."
          >
            {(() => {
              const subtype = inferAssetSubtype({
                symbol: symbol.trim().toUpperCase(),
                asset_class: assetClass,
                watchlist_role: null,
                theme_tags: [],
              });
              const applicable = applicableStrategiesForSubtype(subtype);
              const subtypeLabel: Record<string, string> = {
                quality: 'Quality (Burggraben, hohe Margen)',
                dividend: 'Dividend-Aristokrat',
                momentum: 'Momentum / Wachstum',
                theme: 'Themen-Wert',
                tesla: 'Tesla',
                gold: 'Gold / Edelmetall',
                index_etf: 'Breiter Index-ETF',
                sector_etf: 'Sektor-ETF',
                crypto_anchor: 'Krypto-Anker (BTC/ETH)',
              };
              return (
                <div className="rounded-md border border-zinc-800 bg-zinc-900/50 p-3 space-y-2">
                  <div className="flex items-start gap-2 text-xs">
                    <Info className="size-3.5 text-zinc-500 mt-0.5 shrink-0" />
                    <div className="text-zinc-400">
                      Erkannter Asset-Typ:{' '}
                      <strong className="text-zinc-100">
                        {subtype ? subtypeLabel[subtype] ?? subtype : 'unbekannt'}
                      </strong>
                    </div>
                  </div>
                  {applicable.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {applicable.map((sid) => {
                        const expl = STRATEGY_EXPLANATIONS[sid];
                        return (
                          <span
                            key={sid}
                            className="rounded-full bg-emerald-900/30 border border-emerald-700/40 px-2 py-0.5 text-[11px] text-emerald-200"
                            title={expl?.oneLineExplanation}
                          >
                            {expl?.shortLabel ?? sid}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-[11px] text-amber-300 italic">
                      Für diesen Asset-Typ greift in V5.1 noch keine spezifische Strategie. Score
                      wird neutral berechnet.
                    </div>
                  )}
                  <div className="text-[10px] text-zinc-500 leading-snug pt-1 border-t border-zinc-800">
                    Hintergrund: V5.1 ordnet Strategien anhand <em>asset_subtype</em> automatisch
                    zu. Quality-Werte bekommen z.B. Pullback + RS + Long-Term + Event, Krypto nur
                    den Crypto-Donchian, Gold nur Defensive.
                  </div>
                </div>
              );
            })()}
          </Field>

          {/* Notiz */}
          <Field label="Notiz (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="z.B. Earnings am 30.04., Pivot 178.50"
              className="w-full rounded-md bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm focus-ring resize-none"
            />
          </Field>

          {/* Tags */}
          <Field label="Tags (Komma-getrennt)" hint="z.B. tech, dividend, watchout-earnings">
            <input
              type="text"
              value={tagsRaw}
              onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="tech, highscore"
              className="w-full rounded-md bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm focus-ring"
            />
          </Field>

          {errorMsg && (
            <div className="rounded-md border border-red-900/60 bg-red-950/30 p-3 text-xs text-red-200 flex items-start gap-2">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <span>Speichern fehlgeschlagen: {errorMsg}</span>
            </div>
          )}
        </form>

        <footer className="flex items-center justify-end gap-2 p-5 border-t border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 focus-ring"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!symbolValid || submitting}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-medium text-white focus-ring',
              !symbolValid || submitting
                ? 'bg-zinc-700 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500'
            )}
          >
            {submitting
              ? 'Speichere…'
              : isEditMode
                ? 'Änderungen speichern'
                : 'Hinzufügen'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-300 mb-1">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <div className="mt-1 text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}
